import { useEffect } from "react";
import { MenuItem, TextField, IconButton, Button } from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { SchemaFields } from "./FieldRenderer";
import {
  sectionsForArea,
  fieldsForArea,
  areFieldsComplete,
} from "./specSheetConfig";

const options = {
  Conventional: {
    depths: ["7-1/4", "9-1/4", "11-1/4", "Other"],
    spacings: ["12", "16", "19.2", "24"],
    deflections: ["N/A"],
  },
  "I-Joist": {
    depths: [
      "9-1/2 (Special Order)",
      "11-7/8",
      "14",
      "16 (Special Order)",
      "18 (Special Order, Full units only)",
      "20 (Special Order, Full units only)",
      "Other",
    ],
    spacings: ["12", "16", "19.2", "24"],
    deflections: ["L/360", "L/480", "L/600", "L/720"],
  },
  "Floor Trusses": {
    depths: [
      "12",
      "14",
      "16",
      "18",
      "20",
      "22",
      "24",
      "26",
      "28",
      "30",
      "Other",
    ],
    spacings: ["12", "16", "19.2", "24"],
    deflections: ["L/360", "L/480", "L/600", "L/720"],
  },
  "Open Joist (All Special Order)": {
    depths: ["9-1/4", "11-7/8", "14", "16", "18", "20", "Other"],
    spacings: ["12", "16", "19.2", "24"],
    deflections: ["L/360", "L/480", "L/600", "L/720"],
  },
  Other: { depths: ["Other"], spacings: ["Other"], deflections: ["Other"] },
};

export default function FloorSection(props) {
  const additionalSections = sectionsForArea(props.config, "floors");
  const additionalFields = fieldsForArea(props.config, "floors");

  useEffect(() => {
    const allFilled = props.levels.every((level) =>
      level.materials.every(
        (material) =>
          material.floorMaterial &&
          material.memberDepth &&
          material.spacing &&
          material.deflection
      )
    );
    props.setIsComplete(allFilled);
  }, [props.levels]);

  // Additional-details completion, from the admin-managed schema
  useEffect(() => {
    props.setAdditionFloorFieldsCompleted(
      areFieldsComplete(additionalFields, props.formValues)
    );
  }, [props.formValues, props.config]);

  const handleAdditionalChange = (fieldId, value) => {
    props.setFormValues({ ...props.formValues, [fieldId]: value });
  };

  const handleChange = (levelIndex, materialIndex, field, value) => {
    const newLevels = [...props.levels];
    newLevels[levelIndex].materials[materialIndex][field] = value;

    if (field === "floorMaterial") {
      newLevels[levelIndex].materials[materialIndex] = {
        floorMaterial: value,
        customFloorMaterial: "",
        memberDepth: "",
        customDepth: "",
        spacing: "",
        customSpacing: "",
        deflection: "",
        customDeflection: "",
      };
    }

    props.setLevels(newLevels);
  };

  const addLevel = () => {
    props.setLevels([
      ...props.levels,
      {
        materials: [
          {
            floorMaterial: "",
            customFloorMaterial: "",
            memberDepth: "",
            customDepth: "",
            spacing: "",
            customSpacing: "",
            deflection: "",
            customDeflection: "",
          },
        ],
      },
    ]);
  };

  const deleteLevel = (index) => {
    props.setLevels(props.levels.filter((_, i) => i !== index));
  };

  const addMaterial = (levelIndex) => {
    const newLevels = [...props.levels];
    newLevels[levelIndex].materials.push({
      floorMaterial: "",
      customFloorMaterial: "",
      memberDepth: "",
      customDepth: "",
      spacing: "",
      customSpacing: "",
      deflection: "",
      customDeflection: "",
    });
    props.setLevels(newLevels);
  };

  const deleteMaterial = (levelIndex, materialIndex) => {
    const newLevels = [...props.levels];
    newLevels[levelIndex].materials.splice(materialIndex, 1);
    props.setLevels(newLevels);
  };

  return (
    <div>
      <h3 className="app-muted mb-3 text-xs font-semibold uppercase tracking-wider">
        Floor System by Level
      </h3>
      {props.basicOnly && (
        <p className="app-muted mb-4 text-sm">
          Only the floor bearing info below is needed for a roof-only quote.
        </p>
      )}
      {props.levels.map((level, levelIndex) => (
        <div
          key={levelIndex}
          className="app-surface mb-5 rounded-xl border p-4"
        >
          <div className="mb-4 flex items-center justify-between gap-2">
            <h3 className="text-sm font-bold tracking-wide tabular-nums">
              LEVEL {levelIndex + 1}
            </h3>
            {props.levels.length > 1 && (
              <IconButton
                color="error"
                size="small"
                onClick={() => deleteLevel(levelIndex)}
                aria-label={`Delete level ${levelIndex + 1}`}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </div>

          {level.materials.map((material, materialIndex) => (
            <div
              key={materialIndex}
              className="app-divider mb-4 grid grid-cols-1 items-center gap-4 border-b pb-4 sm:grid-cols-2 lg:grid-cols-4"
            >
              <TextField
                select
                label="Floor Material"
                value={material.floorMaterial}
                onChange={(e) =>
                  handleChange(
                    levelIndex,
                    materialIndex,
                    "floorMaterial",
                    e.target.value
                  )
                }
                fullWidth
                required
              >
                {Object.keys(options).map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>

              {material.floorMaterial === "Other" && (
                <TextField
                  label="Enter Custom Material"
                  value={material.customFloorMaterial}
                  onChange={(e) =>
                    handleChange(
                      levelIndex,
                      materialIndex,
                      "customFloorMaterial",
                      e.target.value
                    )
                  }
                  fullWidth
                />
              )}

              <TextField
                select
                label="Member Depth"
                value={material.memberDepth}
                onChange={(e) =>
                  handleChange(
                    levelIndex,
                    materialIndex,
                    "memberDepth",
                    e.target.value
                  )
                }
                fullWidth
                required
                disabled={!material.floorMaterial}
              >
                {(options[material.floorMaterial]?.depths || []).map(
                  (option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  )
                )}
              </TextField>

              {material.memberDepth === "Other" && (
                <TextField
                  label="Enter Custom Depth"
                  value={material.customDepth}
                  onChange={(e) =>
                    handleChange(
                      levelIndex,
                      materialIndex,
                      "customDepth",
                      e.target.value
                    )
                  }
                  fullWidth
                />
              )}

              <TextField
                select
                label="OC Spacing"
                value={material.spacing}
                onChange={(e) =>
                  handleChange(
                    levelIndex,
                    materialIndex,
                    "spacing",
                    e.target.value
                  )
                }
                fullWidth
                required
                disabled={!material.floorMaterial}
              >
                {(options[material.floorMaterial]?.spacings || []).map(
                  (option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  )
                )}
              </TextField>

              {/* Custom Spacing Input */}
              {material.spacing === "Other" && (
                <TextField
                  label="Enter Custom Spacing"
                  value={material.customSpacing}
                  onChange={(e) =>
                    handleChange(
                      levelIndex,
                      materialIndex,
                      "customSpacing",
                      e.target.value
                    )
                  }
                  fullWidth
                />
              )}

              <TextField
                select
                label="Deflection Criteria"
                value={material.deflection}
                onChange={(e) =>
                  handleChange(
                    levelIndex,
                    materialIndex,
                    "deflection",
                    e.target.value
                  )
                }
                fullWidth
                required
                disabled={!material.floorMaterial}
              >
                {(options[material.floorMaterial]?.deflections || []).map(
                  (option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  )
                )}
              </TextField>
              {/* Custom Deflection Input */}
              {material.deflection === "Other" && (
                <TextField
                  label="Enter Custom Deflection"
                  value={material.customDeflection}
                  onChange={(e) =>
                    handleChange(
                      levelIndex,
                      materialIndex,
                      "customDeflection",
                      e.target.value
                    )
                  }
                  fullWidth
                />
              )}

              {/* Delete Material Button */}
              {level.materials.length > 1 && (
                <div>
                  <IconButton
                    color="error"
                    onClick={() => deleteMaterial(levelIndex, materialIndex)}
                    aria-label="Delete floor material"
                  >
                    <DeleteIcon />
                  </IconButton>
                </div>
              )}
            </div>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={() => addMaterial(levelIndex)}
          >
            Add Floor Material
          </Button>
        </div>
      ))}

      <Button startIcon={<AddIcon />} variant="outlined" onClick={addLevel}>
        Add Level
      </Button>

      {/* Roof-only quotes just need the bearing info above */}
      {!props.basicOnly &&
        additionalSections.map((section) => (
          <div key={section.id} className="mt-8">
            <h3 className="app-muted mb-3 text-xs font-semibold uppercase tracking-wider">
              {section.title}
            </h3>
            <SchemaFields
              fields={section.fields}
              values={props.formValues}
              onChange={handleAdditionalChange}
              columns={section.columns || 2}
            />
          </div>
        ))}
    </div>
  );
}
